<template>
  <div>
    <p class="lead">
      ({{ issues.length }} {{ issues.length !== 1 ? 'orders' : 'order' }}
      {{ issues.length !== 1 ? 'need' : 'needs' }} review)
    </p>

    <table class="table table-hover align-middle">
      <thead>
        <tr>
          <th>Date</th>
          <th>Order</th>
          <th>Item</th>
          <th class="text-end">Cost of Goods</th>
          <th class="text-end">Action</th>
        </tr>
      </thead>
      <transition mode="out-in">
        <tbody v-if="loading">
          <tr><td colspan="5" class="p-5"><loading-spinner /></td></tr>
        </tbody>

        <tbody v-else-if="issues.length === 0">
          <tr>
            <td colspan="5">
              <div class="alert alert-success my-3">
                <i class="bi bi-hand-thumbs-up me-2"></i>
                All cost of goods are accounted for.
              </div>
            </td>
          </tr>
        </tbody>

        <tbody v-else>
          <template v-for="issue in issues" :key="issue.order_id">
            <tr>
              <td class="small">{{ new Date(issue.order_date).toLocaleDateString() }}</td>
              <td>
                <button type="button" class="btn btn-link p-0" @click="openOrder(issue.order_id)">
                  {{ issue.order_id }}
                </button>
              </td>
              <td class="small text-truncate" style="max-width: 200px" :title="issue.item_name">{{ issue.item_name }}</td>
              <td class="text-end"><money-block :dollar="issue.cogs_amount" /></td>
              <td class="text-end">
                <div class="d-flex gap-2 justify-content-end">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    :disabled="!!processingId"
                    @click="handleReverse(issue)"
                    title="Reverse COGS"
                  >
                    <span v-if="processingId === issue.order_id + ':reverse'" class="spinner-border spinner-border-sm"></span>
                    <span v-else><i class="bi bi-arrow-counterclockwise me-1"></i>Reverse</span>
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary"
                    :disabled="!!processingId"
                    @click="openDismiss(issue)"
                    title="Dismiss — item not returned"
                  >
                    <i class="bi bi-eye-slash"></i>
                  </button>
                </div>
              </td>
            </tr>

            <!-- No source price — manual entry prompt -->
            <tr v-if="noSourcePriceIds.has(issue.order_id)">
              <td colspan="5" class="bg-light py-2 px-3">
                <div class="d-flex align-items-center gap-2 small">
                  <i class="bi bi-info-circle text-warning"></i>
                  <span class="text-muted">No source price on record for this item.</span>
                  <a href="#" @click.prevent="openManualEntry(issue.order_id)">Enter amount manually</a>
                </div>
              </td>
            </tr>

            <!-- Inline dismiss row -->
            <tr v-if="dismissingId === issue.order_id">
              <td colspan="5" class="bg-light py-2 px-3">
                <div class="d-flex gap-2 align-items-center">
                  <span class="small text-muted text-nowrap">Item not returned?</span>
                  <input
                    type="text"
                    v-model="dismissReason"
                    class="form-control form-control-sm"
                    placeholder="Reason (optional) — e.g. lost in post, goodwill refund"
                    @keyup.enter="confirmDismiss(issue)"
                    @keyup.escape="closeDismiss"
                  />
                  <button class="btn btn-sm btn-outline-secondary text-nowrap" :disabled="!!processingId" @click="confirmDismiss(issue)">
                    <span v-if="processingId === issue.order_id + ':dismiss'" class="spinner-border spinner-border-sm"></span>
                    <span v-else>Keep COGS</span>
                  </button>
                  <button class="btn btn-sm btn-link text-nowrap" @click="closeDismiss">Cancel</button>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </transition>
    </table>

    <!-- Footer: scan window controls -->
    <div class="d-flex align-items-center gap-3 mt-2">
      <span class="small text-muted">
        {{ scanAll ? 'Showing all orders' : 'Showing last 45 days' }}
      </span>
      <button
        v-if="!scanAll"
        type="button"
        class="btn btn-sm btn-outline-secondary"
        :disabled="loading"
        @click="loadAll"
      >
        <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
        Scan all orders
      </button>
      <span v-if="!scanAll" class="small text-muted fst-italic">May take a moment for large stores</span>
    </div>

    <!-- Inline order detail modal — refreshes table on close -->
    <app-modal :modal-in="!!viewingOrderId" @close="closeOrder" modalClass="modal-lg">
      <template #header>Order {{ viewingOrderId }}</template>
      <div class="container">
        <platform-order :order-id="viewingOrderId" />
      </div>
    </app-modal>

    <!-- Manual COGS reversal entry (when no source_price) — pre-set to CREDIT -->
    <transaction-editor
      :show="showManualEditor"
      :order-id="manualOrderId"
      transaction-type="COGS"
      initial-booking-entry="CREDIT"
      @saved="onManualSaved"
      @close="showManualEditor = false"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useStore } from 'vuex'
import LoadingSpinner from '@/app/components/Utils/LoadingSpinner.vue'
import MoneyBlock from '@/app/components/Utils/MoneyBlock.vue'
import AppModal from '@/app/components/Utils/AppModal.vue'
import PlatformOrder from '@/app/components/ebay/PlatformOrder.vue'
import TransactionEditor from '@/app/components/transactions/TransactionEditor.vue'

const store = useStore()
const issues = ref([])
const loading = ref(false)
const scanAll = ref(false)
const processingId = ref(null)
const dismissingId = ref(null)
const dismissReason = ref('')
const noSourcePriceIds = ref(new Set())

// Inline order modal state
const viewingOrderId = ref(null)

// Manual fallback editor state
const showManualEditor = ref(false)
const manualOrderId = ref(null)

async function loadIssues(days = 45) {
  loading.value = true
  try {
    issues.value = await store.dispatch('eshop/getUnreversedCogsOrders', { days })
    noSourcePriceIds.value = new Set() // clear stale flags on reload
  } catch (e) {
    alert(e.message || 'Failed to load COGS issues')
  }
  loading.value = false
}

async function loadAll() {
  scanAll.value = true
  await loadIssues(null)
}

function openOrder(orderId) {
  viewingOrderId.value = orderId
}

function closeOrder() {
  viewingOrderId.value = null
  // Refresh: the user may have resolved the issue from within the order detail
  loadIssues(scanAll.value ? null : 45)
}

async function handleReverse(issue) {
  processingId.value = issue.order_id + ':reverse'
  noSourcePriceIds.value.delete(issue.order_id)
  try {
    const result = await store.dispatch('eshop/reverseCogs', { orderId: issue.order_id })
    if (result.success) {
      issues.value = issues.value.filter(i => i.order_id !== issue.order_id)
    } else if (result.noSourcePrice) {
      // Show inline prompt instead of silently opening the editor
      noSourcePriceIds.value = new Set([...noSourcePriceIds.value, issue.order_id])
    } else {
      alert(result.error || result.errors?.join('\n') || 'Reversal failed')
    }
  } catch (e) {
    alert(e.message || 'Reversal failed')
  }
  processingId.value = null
}

function openManualEntry(orderId) {
  manualOrderId.value = orderId
  showManualEditor.value = true
}

function openDismiss(issue) {
  dismissingId.value = issue.order_id
  dismissReason.value = ''
}

function closeDismiss() {
  dismissingId.value = null
  dismissReason.value = ''
}

async function confirmDismiss(issue) {
  processingId.value = issue.order_id + ':dismiss'
  try {
    await store.dispatch('eshop/dismissCogs', { orderId: issue.order_id, reason: dismissReason.value })
    issues.value = issues.value.filter(i => i.order_id !== issue.order_id)
    closeDismiss()
  } catch (e) {
    alert(e.message || 'Dismiss failed')
  }
  processingId.value = null
}

function onManualSaved() {
  showManualEditor.value = false
  if (manualOrderId.value) {
    issues.value = issues.value.filter(i => i.order_id !== manualOrderId.value)
    noSourcePriceIds.value.delete(manualOrderId.value)
  }
  manualOrderId.value = null
}

onMounted(() => loadIssues(45))
</script>
